import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { useStateMachine } from "little-state-machine";
import { updateAddressBookData } from "store/store";

import { getCompanyAddressBook, getCompanyAddressV2Book } from "api";
import { Address } from "api/models";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";

interface useScheduleAddressProps {
  typeKey: string;
}

export default function useScheduleAddress({ typeKey }: useScheduleAddressProps) {
  const intl = useIntl();
  const { getValues, watch } = useFormContext();

  const [addressBook, setAddressBook] = useState<Address[]>([]);
  const [searchPlace, setSearchPlace] = useState(getValues(`schedule.${typeKey}_address_name`) || "");

  const { state, actions } = useStateMachine({ updateAddressBookData });

  const { permissions } = usePermissions();
  const { enable_new_address_structure } = permissions ? JSON.parse(permissions)?.company_shipper_settings : null;

  const getAddressBookAPI: any = enable_new_address_structure ? getCompanyAddressV2Book : getCompanyAddressBook;

  const location = getValues("organizational.location") || getValues("shipment.location");

  const handleSearchAddress = (results: any) => {
    if (results?.length > 0) {
      actions.updateAddressBookData({
        addressBook: results,
      });
      return setAddressBook(results);
    }
    actions.updateAddressBookData({
      addressBook: [],
    });
    return setAddressBook([]);
  };

  const getAddresses = () => {
    if (!location) return;
    if (searchPlace?.length > 0) return;
    getAddressBookAPI({
      params: {
        payment_location: location,
        page_size: 20,
      },
    })
      .then((res: AxiosResponse) => {
        setAddressBook(res.data.results);
        actions.updateAddressBookData({
          addressBook: res.data.results,
        });
      })
      .catch((err: any) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.schedule.couldNotRetrieveAddressBook",
            defaultMessage: "Could not retrieve address book. Please try again later.",
          }),
        ),
      );
  };

  useEffect(() => {
    getAddresses();
  }, [watch("organizational.location"), watch("shipment.location")]);

  useEffect(() => {
    const addressSearchAbortController = new AbortController();
    if (searchPlace === "") {
      setAddressBook(state?.addressBook);
    }
    if (!location) return;
    const timer = setTimeout(() => {
      getAddressBookAPI({
        params: {
          payment_location: location,
          query: searchPlace,
          page_size: 20,
        },
        signal: addressSearchAbortController.signal,
      }).then((res: any) => handleSearchAddress(res.data?.results));
    }, 300);
    return () => {
      clearTimeout(timer);
      addressSearchAbortController.abort();
    };
  }, [searchPlace]);

  return { addressBook, searchPlace, setSearchPlace, getAddresses };
}
